/**
 * Sentry initialization for Inertia/React pages.
 * Uses @sentry/react for React-specific error boundaries.
 *
 * Only initializes on pages with a #app root element (Inertia pages).
 * Non-Inertia pages (admin, support, docs, login) are handled by
 * sentry-browser.ts via @sentry/browser.
 *
 * CONEK-4225: Sentry must never break the host app. All init is wrapped in
 * try/catch and browserTracingIntegration is removed (it monkey-patches
 * global fetch(), which propagated relay failures to user-facing requests).
 */
import * as Sentry from "@sentry/react"

// Runtime config from Rails-rendered <meta> tags in application.html.erb.
// One Docker image works across dev/staging/prod because the DSN host
// (which points at relay.conek.<env>) is injected per-pod via ENV rather
// than baked at Vite build time. See CONEK-4225.
//
// Inertia SSR is not enabled today, but this module is imported transitively
// by every Inertia page — the `typeof document` guard keeps it import-safe
// if SSR ever gets flipped on.
const metaContent = (name: string): string | undefined => {
  if (typeof document === "undefined") return undefined
  return document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
    ?.content
}

const dsn = metaContent("sentry-dsn")
const environment = metaContent("sentry-environment") ?? "development"
const isProduction = environment === "production"
const isInertiaPage =
  typeof document !== "undefined" && document.getElementById("app") !== null

if (dsn && isInertiaPage) {
  try {
    Sentry.init({
      dsn,
      environment,

      // browserTracingIntegration is intentionally omitted. It wraps global
      // fetch() to create performance spans. When the Sentry relay/ingest
      // endpoint is unreachable, the wrapper can propagate errors to unrelated
      // user requests (e.g. Inertia router visits). Error capture + session
      // replay work without it.

      // Session Replay: 10% of sessions in production, none elsewhere; always
      // 100% on error. Dev and staging report to production's Sentry project
      // and spend its quota, and recording every session there exhausted it:
      // the relay answered 429 to every page's uploads (CONEK-7732). A
      // session that errors is still recorded, which is the one worth having.
      replaysSessionSampleRate: isProduction ? 0.1 : 0,
      replaysOnErrorSampleRate: 1.0,

      integrations: [Sentry.replayIntegration()],

      // Filter out noise
      ignoreErrors: [
        // Browser extension errors
        "ResizeObserver loop",
        // Network errors from flaky connections
        "Failed to fetch",
        "NetworkError",
        "Load failed",
      ],
    })
  } catch {
    // Sentry init failed — silently continue. The app must never crash
    // because telemetry is broken.
  }
}

export { Sentry }
